import classnames from 'classnames';
import { useBlackoutDates } from 'components/common-form/hooks/useBlackoutDates';
import { MonoDatePickerWithDefaultPreset } from 'components/shared/DatePicker';
import { CurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { usePasteSingleDate } from 'hooks/usePasteSingleDate';
import { useUnderlyingContext } from 'hooks/useUnderlyingContext';
import type { CalendarInfo, StrikeMetadata } from 'models/forms/fields';
import { ChangeEvent, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { UnderlyingStrikeRestriction } from 'shared/services/backends/underlyingBackend.service';
import styled from 'styled-components';
import { camelCaseToWords } from 'utils/string';

import { StyledSpot, StyledStrikeDate, StyledStrikeMode } from './Data.styles';

interface Props {
  restriction: UnderlyingStrikeRestriction;
  strikeDate?: Nullable<string>;
  strikeMode: string;
  strikeValue?: Nullable<number>;
  underlyingId: string;

  calendar?: CalendarInfo;
  currency: string;

  strikeMetadata: StrikeMetadata;
}
const STRIKE_MODES_WITH_READONLY_DATES: string[] = ['EXEC', 'LIVE'];
const STRIKE_MODES_WITH_READONLY_SPOT: string[] = ['OPEN', 'LIVE', 'CLOSE'];

const StyledTooltip = styled(Tooltip)`
  & > .tooltip-inner {
    padding: 0;
  }
`;

export function Data({
  restriction,
  strikeDate,
  strikeMode,
  strikeValue,
  underlyingId,
  calendar,
  currency,
  strikeMetadata: { showMode, showDate, showSpot },
}: Props) {
  const { formatMessage } = useIntl();
  const { onUnderlyingStrikeChanged, strikeModes, isLoading, getCellHighlight } = useUnderlyingContext();
  const calendars = calendar !== undefined ? [calendar] : undefined;
  const blackoutDates = useBlackoutDates(calendars);
  const isStrikeDateReadonly = useMemo(() => STRIKE_MODES_WITH_READONLY_DATES.includes(strikeMode), [strikeMode]);

  const isSpotDisabled = useMemo(
    () =>
      isLoading ||
      restriction === UnderlyingStrikeRestriction.RestrictStrikeValue ||
      restriction === UnderlyingStrikeRestriction.RestrictAll ||
      STRIKE_MODES_WITH_READONLY_SPOT.includes(strikeMode),
    [isLoading, restriction, strikeMode],
  );

  const [spotValue, setSpotValue] = useState<number | string>(strikeValue ?? '');

  const changeStrikeDate = useCallback(
    (newDate: Nullable<string>) => {
      if (typeof onUnderlyingStrikeChanged === 'function') {
        onUnderlyingStrikeChanged(underlyingId, 'strikeDate', newDate);
      }
    },
    [onUnderlyingStrikeChanged, underlyingId],
  );

  const changeStrikeMode = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.currentTarget;

      if (isLoading || typeof underlyingId === 'undefined' || typeof onUnderlyingStrikeChanged !== 'function') {
        return;
      }

      onUnderlyingStrikeChanged(underlyingId, 'strikeMode', value);
    },
    [isLoading, onUnderlyingStrikeChanged, underlyingId],
  );

  const onSpotBlurred = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isLoading || typeof underlyingId === 'undefined' || typeof onUnderlyingStrikeChanged !== 'function') {
        return;
      }

      const { value } = event.currentTarget;
      const sanitizedValue = value === '' || value === '0' ? null : Number(value);

      if ((strikeValue === undefined && sanitizedValue === null) || strikeValue === sanitizedValue) {
        return;
      }

      if (value === '0') {
        setSpotValue('');
      }
      onUnderlyingStrikeChanged(underlyingId, 'strikeValue', sanitizedValue);
    },
    [isLoading, onUnderlyingStrikeChanged, strikeValue, underlyingId],
  );

  const onSpotChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    if (value === '') {
      setSpotValue('');
      return;
    }

    const numericValue = Number(value);
    const sanitizedValue = Number.isNaN(numericValue) ? '' : numericValue;

    setSpotValue(sanitizedValue);
  }, []);

  const onPasteStrikeDate = usePasteSingleDate(changeStrikeDate);
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const strikeModeSelectId = useRef<HTMLTableDataCellElement>(null);
  const isLiveOrExec = strikeMode === 'LIVE' || strikeMode === 'EXEC';

  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const tooltipEnterHandler = () => {
    clearTimeoutRef();
    setIsTooltipOpened(true);
  };

  const tooltipLeaveHandler = () => {
    clearTimeoutRef();
    timeoutRef.current = setTimeout(() => setIsTooltipOpened(false), 300);
  };

  return (
    <>
      {showMode && (
        <StyledStrikeMode
          ref={strikeModeSelectId as RefObject<HTMLTableDataCellElement>}
          className={classnames('border align-middle', getCellHighlight(underlyingId, 'UnderlyingStrikeMode'))}
          onMouseEnter={tooltipEnterHandler}
          onMouseLeave={tooltipLeaveHandler}
        >
          <select
            className="pe-0 form-select fs-6 ps-2"
            data-e2e="underlying-strike-mode"
            data-underlying-id={underlyingId}
            disabled={isLoading}
            onChange={changeStrikeMode}
            value={strikeMode}
          >
            {strikeModes.map(value => (
              <option key={value} value={value}>
                {formatMessage({ id: `form.StrikeMode.${value}` as any, defaultMessage: camelCaseToWords(value) })}
              </option>
            ))}
          </select>
        </StyledStrikeMode>
      )}
      {isLiveOrExec && (
        <Overlay show={isTooltipOpened} target={strikeModeSelectId.current} placement="top">
          <StyledTooltip>
            <div
              className="text-secondary align-middle p-2 bg-lvl1 border border-primary"
              onMouseLeave={() => setIsTooltipOpened(false)}
              onMouseEnter={tooltipEnterHandler}
            >
              <p>{formatMessage({ id: `underlying.strikeMode.${strikeMode}` as any })}</p>
            </div>
          </StyledTooltip>
        </Overlay>
      )}
      {showSpot && (
        <StyledSpot className={classnames('border align-middle', getCellHighlight(underlyingId, 'UnderlyingSpot'))}>
          <div className="with-unit-component input-group">
            <div className="input-group-text text-secondary">
              <CurrencySymbol code={currency} />
            </div>
            <input
              className={classnames('form-control text-end')}
              data-e2e="underlying-strike-spot"
              data-underlying-id={underlyingId}
              disabled={isSpotDisabled}
              onBlur={onSpotBlurred}
              onChange={onSpotChanged}
              placeholder={formatMessage({ id: 'quote.underlyingsGrid.placeholder.spot', defaultMessage: 'Spot' })}
              title={spotValue?.toString()}
              type="number"
              value={spotValue}
            />
          </div>
        </StyledSpot>
      )}
      {showDate && (
        <StyledStrikeDate
          className={classnames('border align-middle', getCellHighlight(underlyingId, 'UnderlyingStrikeDate'))}
        >
          {strikeDate && (
            <MonoDatePickerWithDefaultPreset
              size="md"
              id={`underlying-strike-date-${underlyingId}`}
              value={strikeDate}
              onChange={changeStrikeDate}
              allowWeekend={false}
              isClearable={false}
              disabled={isStrikeDateReadonly || isLoading}
              blackoutDates={blackoutDates}
              onPaste={onPasteStrikeDate}
            />
          )}
        </StyledStrikeDate>
      )}
    </>
  );
}
